import React, { useState, useEffect, useRef } from "react";
import styles from "./gmail-cut-markers-modal.module.css";
import Input from "../../ui/input";
import { MyIcons } from "../../icons/my-icons";
import Button from "../../ui/button";
import Table from "../../ui/table";
import { 
  getGmailCutMarkers, 
  createGmailCutMarker, 
  editGmailCutMarker,
  deleteGmailCutMarker 
} from "../../../services/apiService";

const GmailCutMarkersModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const [formData, setFormData] = useState({
    phrase: "",
    active: true,
  });
  const [markers, setMarkers] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingMarkerId, setEditingMarkerId] = useState(null);

  useEffect(() => {
    const fetchMarkers = async () => {
      try {
        const markersData = await getGmailCutMarkers();
        setMarkers(markersData);
      } catch (error) {
        console.error("Error fetching Gmail cut markers:", error);
      }
    };

    if (isOpen) {
      fetchMarkers();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddMarker = async () => {
    try {
      const markerData = {
        phrase: formData.phrase,
        active: formData.active,
      };

      if (isEditMode && editingMarkerId) {
        // Modo edición
        await editGmailCutMarker(editingMarkerId, markerData);
        console.log("Marcador de corte Gmail editado exitosamente");
      } else {
        // Modo creación
        await createGmailCutMarker(markerData);
        console.log("Marcador de corte Gmail creado exitosamente");
      }
      
      // Refrescar la lista de marcadores
      const markersData = await getGmailCutMarkers();
      setMarkers(markersData);
      
      // Limpiar el formulario y resetear modo edición
      setFormData({
        phrase: "",
        active: true,
      });
      setIsEditMode(false);
      setEditingMarkerId(null);
    } catch (error) {
      console.error("Error al guardar marcador de corte Gmail:", error);
    }
  };

  const handleMarkerClick = (marker) => {
    setFormData({
      phrase: marker.phrase,
      active: marker.active,
    });
    setIsEditMode(true);
    setEditingMarkerId(marker.id);
    
    // Hacer scroll al inicio del modal y focus en el primer input
    setTimeout(() => {
      if (modalRef.current) {
        modalRef.current.scrollTop = 0;
      }
      if (firstInputRef.current) {
        firstInputRef.current.focus();
      }
    }, 100);
  };

  const handleCancel = () => {
    setFormData({
      phrase: "",
      active: true,
    });
    setIsEditMode(false);
    setEditingMarkerId(null);
  };

  const handleDelete = async (marker) => {
    if (window.confirm(`¿Está seguro de que desea eliminar el marcador "${marker.phrase}"?`)) {
      try {
        await deleteGmailCutMarker(marker.id);
        console.log("Marcador de corte Gmail eliminado exitosamente");
        
        // Refrescar la lista
        const markersData = await getGmailCutMarkers();
        setMarkers(markersData);
        
        // Si estábamos editando este marcador, limpiar el formulario
        if (editingMarkerId === marker.id) {
          handleCancel();
        }
      } catch (error) {
        console.error("Error al eliminar marcador de corte Gmail:", error);
        alert("Error al eliminar el marcador. Por favor, intente de nuevo.");
      }
    }
  };

  const headers = [
    { label: "Frase de corte", key: "phrase" },
    { label: "Activo", key: "active" },
    { label: "Acciones", key: "actions" },
  ];

  // Formatear los datos para mostrar "Sí"/"No" en la columna activo
  const formattedMarkers = markers.map(marker => ({
    ...marker,
    active: marker.active ? "Sí" : "No",
    _originalActive: marker.active, // Guardar el valor original
  }));

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} ref={modalRef}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Marcadores de Corte Gmail</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <MyIcons
              name="close"
              width={24}
              height={24}
              color="black"
            />
          </button>
        </div>

        <div className={styles.formSection}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "16px",
              width: "100%",
            }}
          >
            <Input
              ref={firstInputRef}
              label="Frase de corte"
              name="phrase"
              value={formData.phrase}
              onChange={handleInputChange}
              style={{ flex: 1 }}
              placeholder="Ej: --- Reply above this line ---"
            />
          </div>
        </div>

        <div style={{ marginBottom: "16px", display: "flex", gap: "12px" }}>
          <Button 
            type="button" 
            variant="primary" 
            onClick={handleAddMarker}
            disabled={!formData.phrase.trim()}
          >
            {isEditMode ? "GUARDAR CAMBIOS" : "AÑADIR MARCADOR"}
          </Button>
          {isEditMode && (
            <Button type="button" variant="secondary" onClick={handleCancel}>
              CANCELAR
            </Button>
          )}
        </div>

        <div className={styles.tableSection}>
          <Table 
            headers={headers} 
            data={formattedMarkers} 
            onPress={handleMarkerClick}
            onDelete={handleDelete}
            showDeleteButton={true}
          />
        </div>
      </div>
    </div>
  );
};

export default GmailCutMarkersModal;
